import React, { ReactNode, useMemo } from "react";
import { useHasPermission } from "@/hooks";
import { Forbidden, LoadingPage } from "@/pages";

/**
 * Props for ProtectedRoute component
 */
interface ProtectedRouteProps {
  /** The feature name to check permissions for. Defaults to "common" */
  feature?: string;
  /** The permission codes to check against. Required. */
  codes: string[];
  /** The element to render if permissions are granted */
  element: ReactNode;
}

/**
 * ProtectedRoute Component
 * Conditionally renders an element based on user permissions.
 * Uses the useHasPermission hook to check if the user has the required permission codes.
 *
 * @param props - The component props
 * @param props.feature - The feature name to check permissions for
 * @param props.codes - The permission codes to check against
 * @param props.element - The element to render if permissions are granted
 * @returns The element if permissions are granted, otherwise a loading page or Forbidden error component
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  feature: propFeature,
  codes: propCodes,
  element,
}): React.ReactElement => {
  const feature = useMemo(() => propFeature || "common", [propFeature]);
  const codes = useMemo(() => propCodes || [], [propCodes]);

  const { allow, loading } = useHasPermission(codes, feature);

  if (loading) {
    return <LoadingPage />;
  }

  if (!allow) {
    return <Forbidden />;
  }

  return <>{element}</>;
};
